import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
  useRef
} from 'react';
import {
  auth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getRedirectResult,
} from '../firebase';
import { redirectGuard } from './redirectGuard';
import { getIdToken } from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import { User, UserType, AuthError, AuthContextType } from './types'; // Ensure User and UserType are from ./types
import { authUtils } from './utils';
import { useApi } from '../apiClient';
import { Loader2 } from '@pagespeedify/ui'; // Import Loader2

const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');

const AuthContext = createContext<AuthContextType | null>(null);

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Auth error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong with authentication. Please try refreshing.</div>;
    }
    return this.props.children;
  }
}

const processUserData = (profileData: any, firebaseUidFromAuth: string): User => {
  if (!profileData || typeof profileData !== 'object') {
    console.error("Invalid profile data received:", profileData);
    profileData = {}; // Ensure profileData is an object to avoid errors on property access
  }

  // Validate user_type, defaulting if invalid or missing
  const validatedUserType = authUtils.validateUserType(profileData.user_type as string || UserType.UNSUBSCRIBED);

  const processed: User = {
    uid: profileData.firebase_uid || firebaseUidFromAuth, // Firebase UID from backend or auth
    internalUserId: profileData.id || undefined, // Internal User ID from backend (users.id)
    email: profileData.email || null,
    displayName: profileData.displayName || null,
    photoURL: profileData.photoURL || null,
    user_type: validatedUserType,
    subscription_status: profileData.subscription_status || 'inactive', // Default to 'inactive'
    credits: profileData.credits ?? 0, // Default to 0 if null or undefined
    paddle_customer_id: profileData.paddle_customer_id || null, // Assuming this might come from profileData
    is_blocked: typeof profileData.is_blocked === 'boolean' ? profileData.is_blocked : false, // Default to false
    has_billing_history: typeof profileData.has_billing_history === 'boolean' ? profileData.has_billing_history : false, // Default to false
    granted_plan_tier_activation_perks: profileData.granted_plan_tier_activation_perks || [],
    team_id: profileData.team_id || null,
    team_owner_id: profileData.team_owner_id || null,
    is_github_linked: typeof profileData.is_github_linked === 'boolean' ? profileData.is_github_linked : false,
    subscription: profileData.subscription || null,
  };
  // console.log("[AuthContext] Processed profile data (with specific mapping for uid and internalUserId):", processed);
  return processed;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const isMockActive = typeof window !== 'undefined' && localStorage.getItem('useMockUser') === 'true';

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(isMockActive ? true : false);
  const [isLoading, setIsLoading] = useState<boolean>(isMockActive ? false : true);

  const initialMockUser = isMockActive ? {
    uid: 'mock-user-uid',
    internalUserId: '11111111-1111-1111-1111-111111111111',
    email: 'mock-user@swayashin.com',
    user_type: 'basic',
    credits: 1000,
    subscription_status: 'active',
    team_id: null,
    team_owner_id: null,
    token: 'mock-token',
    is_github_linked: false,
    permissions: ['reports:create', 'reports:read', 'tasks:create', 'tasks:read', 'tasks:write', 'monitoring:read', 'monitoring:create']
  } as unknown as User : null;

  const [user, setUser] = useState<User | null>(initialMockUser);
  const [error, setError] = useState<AuthError | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [userType, setUserType] = useState<UserType | null>(isMockActive ? UserType.BASIC : null);
  const [isRefreshingUser, setIsRefreshingUser] = useState<boolean>(false);

  const [currentFirebaseUser, setCurrentFirebaseUser] = useState(auth.currentUser);

  const mergedUserProfile = useMemo(() => {
    if (!user) return null;
    
    if (!currentFirebaseUser) return user;
    
    // Always merge if Firebase has displayName or photoURL, to prefer Firebase data
    const needsMerge = 
      !!(currentFirebaseUser.displayName) ||
      !!(currentFirebaseUser.photoURL);
    
    if (!needsMerge) return user;
    
    return {
      ...user,
      displayName: currentFirebaseUser.displayName || user.displayName,
      photoURL: currentFirebaseUser.photoURL || user.photoURL,
    };
  }, [user, currentFirebaseUser]);

  const lastActivityRef = useRef<number>(Date.now());
  const isProcessingAuthChangeRef = useRef(false);
  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const isLoadingRef = useRef(isLoading);

  const api = useApi();

  const handleLogout = useCallback(async (): Promise<void> => {
    // console.log('[AuthContext] handleLogout called.');
    try {
      await firebaseSignOut(auth);
      // console.log("Logout successful via firebaseSignOut.");
    } catch (error: any) {
      console.error('Logout error:', error);
      setError({ type: 'firebase', message: error.message || 'Logout failed' });
    } finally {
      setUser(null);
      setUserType(null);
      setIsAuthenticated(false);
      authUtils.clearTokens();
      authUtils.clearUserData();
      setIsLoading(false);

      // console.log("Navigating to login page after logout.");
      window.location.href = '/login';
    }
  }, []);

  const syncAndFetchProfile = useCallback(async (uid: string): Promise<User | null> => {
    // console.log('[AuthContext] Syncing user profile...');
    try {
      const response = await api.post<{ success: boolean; user?: any; message?: string }>('/users/sync', null);
      // console.log('[AuthContext] User sync response:', response);

      if (response?.success && response.user) {
        // console.log('[AuthContext] User sync successful, processing profile from sync response.');
        const processedProfile = processUserData(response.user, uid);
        authUtils.storeUserData(processedProfile);
        return processedProfile;
      } else {
        throw new Error(response?.message || 'User sync failed or did not return user profile.');
      }
    } catch (error: any) {
      console.error('[AuthContext] Error during user sync/fetch:', error);
      const message = error.response?.data?.message || error.message || 'User sync or profile fetch failed';
      setError({ type: 'api', message });
      return null;
    }
  }, [api]);

  const updateUserFromFirebase = useCallback(async (firebaseUser: FirebaseUser | null) => {
    if (typeof window !== 'undefined' && localStorage.getItem('useMockUser') === 'true') {
      return;
    }
    // console.log(`[AuthContext] updateUserFromFirebase: Processing user ${firebaseUser?.uid}`);
    if (firebaseUser) {
      try {
        const token = await getIdToken(firebaseUser);
        authUtils.storeToken(token);
        // console.log('[AuthContext] updateUserFromFirebase: Token stored.');

        const profile = await syncAndFetchProfile(firebaseUser.uid);

        if (profile) {
          // Merge Firebase profile data with backend profile data
          const mergedProfile = {
            ...profile,
            displayName: firebaseUser.displayName || profile.displayName,
            photoURL: firebaseUser.photoURL || profile.photoURL,
          };

          // console.log('[AuthContext] Setting user state with merged profile:', mergedProfile);
          setUser(mergedProfile);
          setUserType(profile.user_type || UserType.UNSUBSCRIBED);
          setIsAuthenticated(true);
        } else {
          // If backend sync failed, don't immediately log the user out.
          // Fall back to a minimal profile derived from the Firebase user so UI (avatar etc.) still works.
          console.warn('[AuthContext] Profile fetch/sync returned no profile. Falling back to Firebase profile.');
          const fallbackProfile = processUserData({}, firebaseUser.uid);
          const mergedProfile = {
            ...fallbackProfile,
            email: firebaseUser.email || fallbackProfile.email,
            displayName: firebaseUser.displayName || fallbackProfile.displayName,
            photoURL: firebaseUser.photoURL || fallbackProfile.photoURL,
          };
          setUser(mergedProfile);
          setUserType(fallbackProfile.user_type || UserType.UNSUBSCRIBED);
          setIsAuthenticated(true);
          setError({ type: 'api', message: 'User sync failed; using Firebase profile as fallback.' });
        }
      } catch (error) {
        console.error('[AuthContext] Error processing Firebase user:', error);
        setError({ type: 'firebase', message: 'Error processing user session.' });
        // If we still have a firebaseUser object, fall back to it so the app remains usable.
        if (firebaseUser) {
          console.warn('[AuthContext] Falling back to Firebase user after error.');
          const fallbackProfile = processUserData({}, firebaseUser.uid);
          const mergedProfile = {
            ...fallbackProfile,
            email: firebaseUser.email || fallbackProfile.email,
            displayName: firebaseUser.displayName || fallbackProfile.displayName,
            photoURL: firebaseUser.photoURL || fallbackProfile.photoURL,
          };
          setUser(mergedProfile);
          setUserType(fallbackProfile.user_type || UserType.UNSUBSCRIBED);
          setIsAuthenticated(true);
        } else {
          await handleLogout();
        }
      }
    } else {
      if (userRef.current !== null || isAuthenticatedRef.current) {
        // console.log('[AuthContext] Firebase user is null, ensuring logout state.');
        await handleLogout();
      } else {
        // console.log('[AuthContext] Firebase user is null, already logged out.');
      }
    }
  }, [handleLogout, syncAndFetchProfile]);

  const logoutRef = useRef(handleLogout);
  const updateUserFromFirebaseRef = useRef(updateUserFromFirebase);

  useEffect(() => {
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
    isLoadingRef.current = isLoading;
    logoutRef.current = handleLogout;
    updateUserFromFirebaseRef.current = updateUserFromFirebase;
  });

  // DEV HELPERS: expose a logout helper for end-to-end tests to force unauthenticated state
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
        (window as any).__psf_logout = async () => { try { await handleLogout(); } catch (e) { console.warn('psf_logout error', e); } };
      }
    } catch (e) {
      // noop
    }
    return () => {
      try { if (typeof window !== 'undefined' && (window as any).__psf_logout) { delete (window as any).__psf_logout; } } catch (e) {}
    };
  }, [handleLogout]);

  useEffect(() => {
    // console.log("[AuthContext] AuthProvider Mounted: Setting up onAuthStateChanged listener.");
    isProcessingAuthChangeRef.current = false;

    // Attempt to complete a pending redirect sign-in flow. Some browsers
    // require calling getRedirectResult() after returning from the provider
    // in order to finalize the sign-in. We only call this when a redirect
    // appears likely (redirect guard/session/localStorage or query param).
    //
    // Add a small retry/backoff loop to handle race conditions where the
    // SDK hasn't fully initialised or the internal auth queue ordering
    // delays the redirect result. We keep retries conservative to avoid
    // long blocking on startup; this is mainly to make local/dev and edge
    // cases more reliable.
    (async () => {
      try {
        const LS_FALLBACK_KEY = 'swayashin_google_redirect_msg';
        const hasSessionGuard = !!redirectGuard.getTimestamp();
        let hasLocalFallback = false;
        try {
          if (typeof window !== 'undefined' && 'localStorage' in window) {
            const raw = localStorage.getItem(LS_FALLBACK_KEY);
            if (raw) {
              try {
                const payload = JSON.parse(raw);
                hasLocalFallback = payload?.type === 'initiated';
              } catch (e) {
                // ignore malformed
              }
            }
          }
        } catch (e) {}

        const isGoogleRedirectParam = typeof window !== 'undefined' && window.location && window.location.search && window.location.search.includes('with=google');

        if (hasSessionGuard || hasLocalFallback || isGoogleRedirectParam) {
          try { if (process.env.NODE_ENV !== 'production') console.log('[AuthContext] Detected possible redirect sign-in; attempting getRedirectResult with retries'); } catch(e){}

          // Retry configuration
          const maxAttempts = (process.env.NODE_ENV !== 'production') ? 5 : 1; // be more aggressive in dev
          const baseDelayMs = 250; // initial backoff

          for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
              const result = await getRedirectResult(auth);
              if (result && result.user) {
                // Immediately update app state from the firebase user we got back
                try { await updateUserFromFirebaseRef.current(result.user); } catch (e) { console.warn('[AuthContext] updateUserFromFirebaseRef error after getRedirectResult', e); }
                // Clear redirect guard(s)
                try { redirectGuard.clear(); } catch (e) {}
                try { if (typeof window !== 'undefined' && 'localStorage' in window) localStorage.removeItem(LS_FALLBACK_KEY); } catch (e) {}
                break; // done
              }

              // No user yet. If this was the last attempt, log and attempt a
              // dev-only polling fallback to handle SDK timing races where the
              // redirect result may be delivered via the auth state a short
              // time after getRedirectResult returns. This improves reliability
              // in local/dev environments where the SDK initialization ordering
              // can vary.
              if (attempt === maxAttempts) {
                try { console.warn('[AuthContext] getRedirectResult returned no user after retries'); } catch (e) {}

                // In dev only, poll auth.currentUser for a short duration and
                // accept it if it becomes available.
                try {
                  if (process.env.NODE_ENV !== 'production') {
                    try { console.log('[AuthContext] dev fallback: polling auth.currentUser for up to 5s'); } catch(e){}
                    const pollMaxMs = 5000;
                    const pollInterval = 200;
                    let waited = 0;
                    while (waited < pollMaxMs) {
                      if (auth && auth.currentUser) {
                        try { console.log('[AuthContext] dev fallback: auth.currentUser observed, finalizing login'); } catch(e){}
                        try { await updateUserFromFirebaseRef.current(auth.currentUser); } catch (e) { console.warn('[AuthContext] dev fallback updateUserFromFirebase error', e); }
                        try { redirectGuard.clear(); } catch (e) {}
                        try { if (typeof window !== 'undefined' && 'localStorage' in window) localStorage.removeItem(LS_FALLBACK_KEY); } catch (e) {}
                        break;
                      }
                      // wait
                      await new Promise((res) => setTimeout(res, pollInterval));
                      waited += pollInterval;
                    }
                    if (!auth || !auth.currentUser) {
                      try { console.warn('[AuthContext] dev fallback: no auth.currentUser after polling'); } catch(e){}
                    }
                  }
                } catch (e) {
                  // swallow dev-only fallback errors to avoid breaking startup
                  try { console.warn('[AuthContext] dev fallback polling failed', e); } catch(e){}
                }

                break;
              }
            } catch (e) {
              // If the SDK returns errors due to initialization/race conditions,
              // retry a few times in dev. Log a concise warning on the last attempt.
              if (attempt === maxAttempts) {
                console.warn('[AuthContext] getRedirectResult failed after retries', e);
              }
            }

            // Exponential-ish backoff with jitter (small)
            const delay = baseDelayMs * attempt + Math.floor(Math.random() * 80);
            await new Promise((res) => setTimeout(res, delay));
          }
        }
      } catch (e) {
        // noop
      }
    })();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (typeof window !== 'undefined' && localStorage.getItem('useMockUser') === 'true') {
        return;
      }
      setCurrentFirebaseUser(firebaseUser);
      const currentAppUser = userRef.current;
      const currentIsLoading = isLoadingRef.current;
      const currentIsAuthenticated = isAuthenticatedRef.current;

      // console.log(`[AuthContext] onAuthStateChanged triggered. FB User: ${firebaseUser?.uid || null}, App User: ${currentAppUser?.uid || null}, Auth: ${currentIsAuthenticated}, Loading: ${currentIsLoading}`);

      if (isProcessingAuthChangeRef.current) {
        // console.log("[AuthContext] onAuthStateChanged: Skipping update (already processing).");
        return;
      }

      const isLoggedIn = !!firebaseUser;
      const wasLoggedIn = currentIsAuthenticated;
      const uidChanged = firebaseUser?.uid !== currentAppUser?.uid;

      if (isLoggedIn === wasLoggedIn && !uidChanged && !currentIsLoading) {
        // console.log("[AuthContext] onAuthStateChanged: Skipping update (auth state/UID unchanged and not loading).");
        return;
      }

      // console.log("[AuthContext] onAuthStateChanged: Auth state change detected, proceeding.");
      isProcessingAuthChangeRef.current = true;
      if (!currentIsLoading) {
        setIsLoading(true);
      }

      try {
        await updateUserFromFirebaseRef.current(firebaseUser);
      } catch (error) {
        console.error("[AuthContext] Error during updateUserFromFirebase from onAuthStateChanged:", error);
        if (userRef.current !== null || isAuthenticatedRef.current) {
          await logoutRef.current();
        }
      } finally {
        if (isLoadingRef.current) {
          setIsLoading(false);
        }
        isProcessingAuthChangeRef.current = false;
        // console.log("[AuthContext] onAuthStateChanged: Processing complete.");
      }
    });

    const initialCheckTimeout = setTimeout(() => {
      if (isLoadingRef.current && !auth.currentUser) {
        // console.log("[AuthContext] Initial auth check: No user found after timeout, setting loading false.");
        setIsLoading(false);
        isProcessingAuthChangeRef.current = false;
      }
    }, 2500);

    return () => {
      // console.log("[AuthContext] AuthProvider Unmounted: Cleaning up listener.");
      unsubscribe();
      clearTimeout(initialCheckTimeout);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    let inactivityTimer: NodeJS.Timeout;
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const checkInactivity = () => {
      const thirtyMinutes = 30 * 60 * 1000;
      if (Date.now() - lastActivityRef.current > thirtyMinutes) {
        // console.log("[AuthContext] Logging out due to inactivity.");
        handleLogout();
        setError({ type: 'expired', message: 'Session expired due to inactivity' });
      }
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    events.forEach(event => window.addEventListener(event, updateActivity, { passive: true }));
    inactivityTimer = setInterval(checkInactivity, 60000);

    updateActivity();

    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
      clearInterval(inactivityTimer);
    };
  }, [isAuthenticated, handleLogout]);

  const signInWithGoogle = useCallback(async (method?: 'popup' | 'redirect'): Promise<boolean> => {
    setIsLoggingIn(true);
    setError(null);

    // If we are going to redirect, set the redirect guard so other tabs / mounts know
    // a redirect-based sign-in was intentionally initiated. This mirrors logic in
    // the Login page which listens for that timestamp. We also write a small
    // localStorage fallback message for older browsers / cross-tab coordination.
    const prepareForRedirect = () => {
      // Ensure we atomically set both the sessionStorage timestamp (used by the
      // in-app redirect guard) and a localStorage fallback message that can be
      // observed after a full page redirect/round-trip. Keep the fallback key
      // for a short but sufficient window (30s) so the Login page can read it
      // after returning from the provider.
      try {
        const now = Date.now();
        // debug: mark in console before we mutate storage
        try { if (process.env.NODE_ENV !== 'production') console.log('[AuthContext] prepareForRedirect: setting redirect guard', now); } catch (e) {}
        try { redirectGuard.setTimestamp(now); } catch (e) {}

        try {
          if (typeof window !== 'undefined' && 'localStorage' in window) {
            const LS_FALLBACK_KEY = 'swayashin_google_redirect_msg';
            // write fallback key synchronously
            localStorage.setItem(LS_FALLBACK_KEY, JSON.stringify({ type: 'initiated', ts: now }));
            // debug: persist an additional dev key so we can trace the event
            try { if (process.env.NODE_ENV !== 'production') localStorage.setItem('swayashin_google_redirect_debug', String(now)); } catch (e) {}
            // schedule a longer cleanup to ensure the key survives the redirect
            // round-trip back to the app. 30s is conservative but small.
            setTimeout(() => { try { localStorage.removeItem(LS_FALLBACK_KEY); try { localStorage.removeItem('swayashin_google_redirect_debug'); } catch(e){} } catch (e) {} }, 30_000);
          }
        } catch (e) {}
      } catch (e) {
        // swallow
      }
    };

    if (method === 'redirect') {
      try {
        prepareForRedirect();
        try { if (process.env.NODE_ENV !== 'production') console.log('[AuthContext] signInWithGoogle: calling signInWithRedirect'); } catch (e) {}
        await signInWithRedirect(auth, googleProvider);
        return true;
      } catch (redirectError: any) {
        console.error("[AuthContext] signInWithRedirect failed:", redirectError);
        setError({ type: 'credentials', message: redirectError.message || 'Google Sign-In via redirect failed.' });
        setIsLoggingIn(false);
        return false;
      }
    }

    // Default behavior: try popup, fallback to redirect
    try {
      await signInWithPopup(auth, googleProvider);
      return true;
    } catch (error: any) {
      console.warn("[AuthContext] signInWithPopup failed:", error?.code, error?.message);
      // Some browsers or test stubs may throw errors without a standard
      // Firebase error code. Treat untyped errors that contain 'popup'
      // or 'blocked' in the message as popup-blocked equivalents so we
      // fall back to redirect.
      const msg = String(error?.message || '').toLowerCase();
      const isPopupLike = ['auth/popup-closed-by-user', 'auth/popup-blocked', 'auth/cancelled-popup-request'].includes(error?.code)
        || msg.includes('popup') || msg.includes('blocked');
      if (isPopupLike) {
        try {
          prepareForRedirect();
          try { if (process.env.NODE_ENV !== 'production') console.log('[AuthContext] popup fallback -> signInWithRedirect'); } catch (e) {}
          await signInWithRedirect(auth, googleProvider);
          return true;
        } catch (redirectError: any) {
          console.error("[AuthContext] signInWithRedirect failed on fallback:", redirectError);
          setError({ type: 'credentials', message: redirectError.message || 'Google Sign-In via redirect failed.' });
          setIsLoggingIn(false);
          return false;
        }
      } else {
        console.error("[AuthContext] signInWithPopup error (not falling back):", error);
        setError({ type: 'credentials', message: error?.message || 'Google Sign-In via popup failed.' });
        setIsLoggingIn(false);
        return false;
      }
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    if (!currentFirebaseUser) {
      // console.warn('[AuthContext] getAuthToken called but no user is logged in.');
      return null;
    }
    try {
      // console.log('[AuthContext] Getting ID token...');
      const token = await getIdToken(currentFirebaseUser);
      // console.log('[AuthContext] ID token retrieved successfully.');
      return token;
    } catch (error: any) {
      console.error('[AuthContext] Error getting ID token:', error);
      setError({ type: 'firebase', code: error.code || 'auth/get-id-token-error', message: error.message || 'Failed to retrieve authentication token.' });
      return null;
    }
  }, [currentFirebaseUser]);

  const refreshUser = useCallback(async () => {
    if (currentFirebaseUser && !isRefreshingUser) {
      // console.log("[AuthContext] Refreshing user profile manually...");
      setIsRefreshingUser(true);
      setError(null);
      try {
        const profile = await syncAndFetchProfile(currentFirebaseUser.uid);
        if (profile) {
          // Merge Firebase profile data with backend profile data
          const mergedProfile = {
            ...profile,
            displayName: currentFirebaseUser.displayName || profile.displayName,
            photoURL: currentFirebaseUser.photoURL || profile.photoURL,
          };
          
          setUser(mergedProfile);
          setUserType(profile.user_type || UserType.UNSUBSCRIBED);
          // console.log("[AuthContext] Manual profile refresh successful, user state updated.");
        } else {
          console.error("[AuthContext] Manual profile refresh failed to fetch profile.");
        }
      } catch (err) {
        console.error("[AuthContext] Error during manual profile refresh:", err);
      } finally {
        setIsRefreshingUser(false);
      }
    } else if (isRefreshingUser) {
      // console.log("[AuthContext] Refresh already in progress.");
    } else {
      // console.warn("[AuthContext] refreshUser called but no user logged in.");
    }
  }, [syncAndFetchProfile, isRefreshingUser, currentFirebaseUser]);

  const contextValue = useMemo(() => {
    const isAdmin = userType === UserType.ADMIN;
    const isBlocked = mergedUserProfile?.is_blocked ?? (userType === UserType.BLOCKED);
    const hasPermission = (requiredTypes: UserType | UserType[]): boolean => {
      if (!userType || isBlocked) return false;
      const typesArray = Array.isArray(requiredTypes) ? requiredTypes : [requiredTypes];
      return typesArray.includes(userType);
    };

    return {
      user: mergedUserProfile,
      isAuthenticated,
      isLoading,
      isLoggingIn,
      error,
      userType,
      isAdmin,
      isBlocked,
      hasPermission,
      getAuthToken,
      logout: handleLogout,
      signInWithGoogle: signInWithGoogle as (method?: 'popup' | 'redirect') => Promise<boolean>,
      refreshUser,
      clearError,
    };
  }, [
    mergedUserProfile,
    isAuthenticated,
    isLoading,
    isLoggingIn,
    error,
    userType,
    getAuthToken,
    handleLogout,
    signInWithGoogle,
    refreshUser,
    clearError,
  ]);

  if (isLoading && !isAuthenticated) {
    // console.log("AuthProvider: Rendering initial loading state");
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#1a1f35] to-[#0f172a]">
        <Loader2 className="h-12 w-12 text-white animate-spin" />
      </div>
    );
  }

  // console.log(`AuthProvider: Rendering children (isLoading: ${isLoading}, isAuthenticated: ${isAuthenticated})`);
  return (
    <ErrorBoundary>
      <AuthContext.Provider value={contextValue}>
        {children}
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { ErrorBoundary };